/* eslint-disable no-param-reassign */
import React from 'react';
import _ from 'underscore';
import showDialog from 'app/blocks/common/jsx/dialogModal';
import Publisher from 'app/blocks/common/publisher';
import * as utils from 'app/blocks/common/utils';
import * as middleware from 'app/blocks/middleware/middleware';
import StaticContent from 'app/blocks/static-content/static-content';
import OrdersDialogProfileAddresses from 'app/pages/orders/dialog/profile-addresses';
import validation from 'app/pages/orders/panel/contact/validation';
import validationVatTax from '../vat-tax/validation';

export default (p_, handler) => {
    const publisher = Publisher();
    const defaultState = {
        mainAddress: {},
        secondaryAddress: {},
        isSecondaryAddressTheSame: false,
        vatTaxDetails: {},
    };

    const self = {
        publisher,

        state: { ...defaultState, isValidating: false },
        setState(patch) {
            self.state = { ...self.state, ...patch };
            publisher.notify(self.state);
        },
        async validate() {
            try {
                self.setState({ isValidating: true, errors: [] });

                const cleanedAddresses = Object.entries(self.getAddresses()).reduce((obj, [key, address]) => {
                    obj[key] = utils.trimStringProperties(address);
                    return obj;
                }, {});
                validation(cleanedAddresses, false);

                const updatedOrder = {
                    ...p_.getOrder(),
                    vatTaxDetails: self.state.vatTaxDetails,
                };

                await validationVatTax(updatedOrder);
            } finally {
                self.setState({ isValidating: false });
            }
        },
        isChanged() {
            return Object.keys(defaultState).some(
                key => self.state[key] && !_.isEqual(defaultState[key], self.state[key]),
            );
        },
        getAddresses() {
            return {
                mainAddress: self.state.mainAddress,
                secondaryAddress: {},
            };
        },

        async updateAddressWithApprove(previousAddresses, addresses, checkAndApprovePromise) {
            if (!checkAndApprovePromise) {
                checkAndApprovePromise = (prevAddress, currAddress) => Promise.resolve(currAddress);
            }

            const cleanedAddresses = Object.entries(addresses).reduce((obj, [key, address]) => {
                obj[key] = utils.trimStringProperties(address);
                return obj;
            }, {});

            const isSingleAddress = _.isEqual(cleanedAddresses.mainAddress, cleanedAddresses.secondaryAddress);
            const mainAddressSuggestion = await checkAndApprovePromise(
                previousAddresses.mainAddress,
                cleanedAddresses.mainAddress,
            );

            addresses.mainAddress = { ...cleanedAddresses.mainAddress, ...mainAddressSuggestion };

            if (isSingleAddress) {
                addresses.secondaryAddress = { ...cleanedAddresses.secondaryAddress, ...mainAddressSuggestion };
            } else if (!_.isEmpty(cleanedAddresses.secondaryAddress)) {
                const secondaryAddressSuggestion = await checkAndApprovePromise(
                    previousAddresses.secondaryAddress,
                    cleanedAddresses.secondaryAddress,
                );

                addresses.secondaryAddress = { ...cleanedAddresses.secondaryAddress, ...secondaryAddressSuggestion };
            }

            return addresses;
        },
        selectAddresses(addresses) {
            self.setState({ ...addresses });
        },
        handleAddressFlagChange() {
            const isSecondaryAddressTheSame = !self.state.isSecondaryAddressTheSame;

            if (isSecondaryAddressTheSame) {
                self.setState({
                    isSecondaryAddressTheSame,
                    secondaryAddress: { ...self.state.mainAddress },
                });
            } else {
                self.setState({ isSecondaryAddressTheSame });
            }
        },
        updateMainAddress(mainAddress) {
            if (self.state.isSecondaryAddressTheSame) {
                self.setState({ mainAddress, secondaryAddress: { ...mainAddress } });
            } else {
                self.setState({ mainAddress });
            }
        },
        updateSecondaryAddress(secondaryAddress) {
            self.setState({ secondaryAddress });
        },
        updateAddressFromProfile(isMain) {
            showDialog.modal(closeAnd => ({
                type: OrdersDialogProfileAddresses,
                innerContent: (
                    <StaticContent
                        key="order__panel_contact__select_addresses"
                        src="order__panel_contact__select_addresses.html"
                    />
                ),
                addresses: p_.profileAddresses,
                currentCountry: self.state.mainAddress.countryCode,
                disableDifferentCountries: p_.disableDifferentCountrySelection,
                onSelect: address =>
                    closeAnd(() => {
                        address.primaryEmail = p_.emailDetails.primaryEmailAddr;
                        if (isMain) {
                            self.updateMainAddress(address);
                        } else {
                            self.updateSecondaryAddress(address);
                        }
                    }).apply(),
                onClose: closeAnd(),
            }));
        },
        updateMainAddressFromProfile() {
            self.updateAddressFromProfile(true);
        },
        updateSecondaryAddressFromProfile() {
            self.updateAddressFromProfile(false);
        },
        addressesProvider: {
            getInstitutionsPromise: middleware.address.getInstitutions,
            getStatesPromise: parent => middleware.address.getStates.bind(self, parent),
        },
        isUpdateFromProfileButtonEnabled() {
            return _.some(
                _.values(p_.profileAddresses),
                value =>
                    !_.isEmpty(value) &&
                    !(
                        p_.disableDifferentCountrySelection &&
                        !_.isEqual(value.countryCode, self.state.mainAddress.countryCode)
                    ),
            );
        },

        selectVatTax(vatTaxDetails) {
            self.setState({ vatTaxDetails });
        },
        updateVatIdNumber(vatId) {
            const vatIdNumber = vatId ? vatId.trim() : '';
            self.setState({ vatTaxDetails: { ...self.state.vatTaxDetails, vatIdNumber } });
        },
        updateTaxExemptionNumber(taxExemptionNumber) {
            self.setState({ vatTaxDetails: { ...self.state.vatTaxDetails, taxExemptionNumber } });
        },
    };

    publisher.subscribe(handler);
    publisher.notify(self.state);

    return self;
};
